import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../react-auth0-spa';
import CustomButton from '../../components/CustomButton';

// How long we wait for the browser to actually leave for Auth0 before
// offering a manual retry. The redirect normally commits within a few
// hundred ms; anything past this means it isn't coming.
const REDIRECT_GRACE_MS = 6000;

interface Props {
    /** Message from the failed sign-in round trip, when there was one. */
    error?: string | null;
}

/**
 * Shown while the app hands the user over to Auth0.
 *
 * Replaces the former bare `<div>Waiting for redirect ...</div>`, which was a
 * dead end: when the redirect never happened — Auth0 answered with an error,
 * the PKCE transaction was gone, the navigation was blocked — the screen had
 * no retry and no sign-out, so the only way out was quitting the admin UI.
 * Here the retry appears as soon as we know the redirect failed, and after a
 * short grace period even when we don't.
 */
const SignInRedirectPage = ({ error }: Props) => {
    const { t } = useTranslation();
    const { retryLogin, logout } = useAuth0();
    const [graceElapsed, setGraceElapsed] = useState(false);

    useEffect(() => {
        const timer = window.setTimeout(() => setGraceElapsed(true), REDIRECT_GRACE_MS);
        return () => window.clearTimeout(timer);
    }, []);

    const showActions = !!error || graceElapsed;

    return (
        <div className={'flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center'}>
            {!showActions && (
                <div className={'h-10 w-10 animate-spin rounded-full border-b-2 border-primary'} />
            )}
            <p className={'text-base font-medium text-foreground'}>
                {error ? t('sign_in.failed_title') : t('sign_in.redirecting')}
            </p>
            {showActions && (
                <>
                    <p className={'max-w-md text-sm text-foreground/60'}>
                        {t('sign_in.failed_hint')}
                    </p>
                    <div className={'flex flex-wrap items-center justify-center gap-2'}>
                        <CustomButton
                            title={t('sign_in.retry')}
                            onClick={() => retryLogin()}
                            fullWidth={false}
                        />
                        <CustomButton
                            title={t('side_bar.logout')}
                            isFilled={false}
                            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                            fullWidth={false}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default SignInRedirectPage;
